import { useLayoutEffect } from 'react';

import type { MdcMeasure } from '#/lib/performance';
import { endMeasure, MDC_MEASURE, startMeasure } from '#/lib/performance';

/**
 * Closes `mdc:boot` on React's first commit and opens `mdc:data-visible` at the
 * same instant, so the two measures abut rather than overlap.
 *
 * @remarks
 *
 * Mount once, at the app root. The layout effect runs after the first commit's
 * DOM mutations but before paint, which is the moment `mdc:boot` is defined to
 * end. `mdc:boot` is opened by whichever entrypoint mounted the app.
 */
export function useBootMeasure(): void {
  useLayoutEffect(() => {
    endMeasure(MDC_MEASURE.boot);
    startMeasure(MDC_MEASURE.dataVisible);
  }, []);
}

/**
 * Closes `measure` on the first commit where `isReady` is true.
 *
 * @remarks
 *
 * Use this for measures that end when something becomes visible: pass the same
 * condition that makes the content render, so the effect fires immediately
 * after the commit that painted it. Repeat calls are no-ops — see
 * `#/lib/performance`.
 */
export function useMeasureOnCommit(measure: MdcMeasure, isReady: boolean) {
  useLayoutEffect(() => {
    if (isReady) {
      endMeasure(measure);
    }
  }, [isReady, measure]);
}
